import React, { useEffect, useState, useRef } from 'react';
import IncidentNavigationBar from './IncidentNavigationBar';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';

// Confirmation dialog component
const ConfirmationDialog = ({ open, onProceed, onCancel }) => {
  if (!open) return null;
  
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
    }}>
      <div style={{
        background: '#fff', borderRadius: 8, padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        width: '90%', maxWidth: '500px', minWidth: '320px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '18px' }}>Confirm Action</h3>
        <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: '1.5' }}>
          Your saved data will be lost if you proceed. Are you sure you want to continue?
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4,
              background: '#fff', color: '#666', cursor: 'pointer', fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={onProceed}
            style={{
              padding: '8px 16px', border: '1px solid #f93e3e', borderRadius: 4,
              background: '#f93e3e', color: '#fff', cursor: 'pointer', fontSize: '14px'
            }}
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

// Simple modal component
const IframeModal = ({ open, onClose, src, onRequestClose }) => {
  const [size, setSize] = useState({ width: 1200, height: 650 });
  const [resizing, setResizing] = useState(false);
  const modalRef = useRef(null);

  // Reset size to default when modal opens
  useEffect(() => {
    if (open) {
      setSize({ width: 1200, height: 650 });
    }
  }, [open]);

  useEffect(() => {
    function onMouseMove(e) {
      if (!resizing) return;
      const rect = modalRef.current.getBoundingClientRect();
      const newWidth = Math.max(400, e.clientX - rect.left);
      const newHeight = Math.max(300, e.clientY - rect.top);
      setSize({ width: newWidth, height: newHeight });
    }
    function onMouseUp() {
      setResizing(false);
    }
    function onClick() {
      if (resizing) {
        setResizing(false);
      }
    }
    if (resizing) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('click', onClick);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('click', onClick);
    };
  }, [resizing]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !resizing) {
      onRequestClose();
    }
  };

  if (!open) return null;
  return (
    <div 
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 0,
          position: 'relative',
          width: '90%',
          maxWidth: size.width,
          minWidth: '400px',
          height: '80%',
          maxHeight: size.height,
          minHeight: '300px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
          resize: 'none',
          overflow: 'hidden'
        }}
      >
        <div style={{ padding: 12, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'move' }}>
          <span style={{ fontWeight: 600 }}>Select Form Data</span>
          <button 
            onClick={onRequestClose} 
            style={{ 
              position: 'absolute', 
              top: 8, 
              right: 12, 
              zIndex: 2, 
              background: 'rgba(255, 255, 255, 0.9)', 
              border: '1px solid #ddd', 
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              fontSize: '18px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#f93e3e';
              e.target.style.color = '#fff';
              e.target.style.borderColor = '#f93e3e';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.9)';
              e.target.style.color = '#666';
              e.target.style.borderColor = '#ddd';
              e.target.style.transform = 'scale(1)';
            }}
          >
            ×
          </button>
        </div>
        <iframe src={src} title="Custom Iframe" style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }} allow="clipboard-read; clipboard-write"/>
        {/* Resize handle */}
        <div
          onMouseDown={() => setResizing(true)}
          style={{
            position: 'absolute',
            width: 24,
            height: 24,
            right: 0,
            bottom: 0,
            cursor: 'nwse-resize',
            zIndex: 10,
            background: 'transparent'
          }}
        >
          <svg width="24" height="24">
            <polyline points="0,24 24,24 24,0" style={{ fill: 'none', stroke: '#bbb', strokeWidth: 2 }} />
          </svg>
        </div>
      </div>
    </div>
  );
};

// Helper component to sync tryItOutEnabled from SwaggerUI plugin to parent state
const TryItOutSync = ({ value, setValue }) => {
  useEffect(() => {
    setValue(value);
  }, [value, setValue]);
  return null;
};

const IncidentFormDataPlugin = (openModal, setTryItOutEnabled) => () => {
  return {
    wrapComponents: {
      parameters: (Original, system) => (props) => {
        return (
          <>
            <Original {...props} />
            <TryItOutSync value={props.tryItOutEnabled} setValue={setTryItOutEnabled} />
          </>
        );
      },
      ParameterRow: (Original, system) => (props) => {
        console.log('ParameterRow props:', props);
        return (
          <>
            <Original {...props} />
            {props.param && typeof props.param.renderAfterInput === "function"
              ? props.param.renderAfterInput()
              : null}
          </>
        );
      },
      FileField: (Original, system) => (props) => {
        console.log('FileField props:', props);
        return <Original {...props} />;
      },
    },
  };
};

const IncidentIndexPage = () => {
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [tryItOutEnabled, setTryItOutEnabled] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    fetch('/config.json')
      .then(res => res.json())
      .then(config => {
        const caseCreationUrl = config.CASE_CREATION_URL;
        const caseTypeName = "incident";
        setIframeUrl(window.location.origin.replace("-api-explorer", "") + "/" + caseCreationUrl + "?copyJson=true&caseTypeName=" + caseTypeName);
        const baseUrl = config.API_BASE_URL;
        let domain;
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isLocal) {
          domain = config.DOMAIN.replace("https://", "").replace("http://", "").replace("-api-explorer", "");
        } else {
          domain = window.location.hostname.replace("https://", "").replace("http://", "").replace("-api-explorer", "");
        }
        return fetch(`${baseUrl}wolken-secure/openapi/itsm/incident`, { credentials: 'include', headers: { domain } });
      })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch OpenAPI spec');
        return res.json();
      })
      .then(json => {
        setSpec(json);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const handleModalCloseRequest = () => {
    setShowConfirmation(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmation(false);
    setModalOpen(false);
  };

  // Reset modal size when modal is closed
  useEffect(() => {
    if (!modalOpen) {
      // Reset to default size when modal closes
      const modalElement = document.querySelector('[data-modal="iframe-modal"]');
      if (modalElement) {
        // Force re-render with default size by updating the size state
        setTimeout(() => {
          // This will be handled by the IframeModal component
        }, 0);
      }
    }
  }, [modalOpen]);

  const handleCancelClose = () => {
    setShowConfirmation(false);
  };

  // Close modal if Try it out is cancelled
  useEffect(() => {
    if (!tryItOutEnabled && modalOpen) {
      setModalOpen(false);
    }
  }, [modalOpen, tryItOutEnabled]);

  // DOM manipulation: move the button next to the formData input using MutationObserver and retry
  useEffect(() => {
    let observer;
    function moveButton(retries = 10) {
      
      const container = document.getElementById('operations-Incident-createIncidentRequest');

      const input = container
        ? container.querySelector('input[placeholder="data"], textarea[placeholder="data"]')
        : null; 

      const button = buttonRef.current;

      if (input && button && input.parentNode) {
        input.parentNode.insertBefore(button, input.nextSibling);
        if (observer) observer.disconnect(); // Stop observing after moving the button
      } else if (retries > 0) {
        setTimeout(() => moveButton(retries - 1), 100);
      }
    }
    observer = new MutationObserver(() => {
      moveButton();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    moveButton();
    return () => {
      if (observer) observer.disconnect();
    };
  }, [modalOpen, tryItOutEnabled]);

  return (
    <>
      <IncidentNavigationBar />
      <IframeModal 
        open={modalOpen} 
        onClose={() => setModalOpen(false)} 
        onRequestClose={handleModalCloseRequest}
        src={iframeUrl} 
      />
      <ConfirmationDialog 
        open={showConfirmation}
        onProceed={handleConfirmClose}
        onCancel={handleCancelClose}
      />
      <div id="content-div" style={{ marginTop: '2rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', marginTop: '2rem', fontSize: '1.2rem' }}>Loading API documentation...</div>
        ) : error ? (
          <div style={{ color: 'red', textAlign: 'center', marginTop: '2rem' }}>Error: {error}</div>
        ) : (
          <>
            <SwaggerUI spec={spec} plugins={[IncidentFormDataPlugin(() => setModalOpen(true), setTryItOutEnabled)]} />
            {/* Button always rendered, but hidden when not in Try it out */}
            <button
              id="open-iframe-btn"
              ref={buttonRef}
              type="button"
              style={{
                marginLeft: 8,
                padding: "4px 10px",
                borderRadius: 4,
                border: "1px solid #49cc90",
                background: "#fff",
                color: "#49cc90",
                cursor: "pointer",
                fontWeight: 600,
                zIndex: 10,
                position: 'relative',
                display: tryItOutEnabled ? 'inline-block' : 'none',
              }}
              onClick={() => setModalOpen(true)}
            >
              Select Form Data
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default IncidentIndexPage;
